import { useCallback, useState } from 'react';
import { toast } from 'react-toastify';

import { history } from '../../../../../helpers';

import type { PlanMutations, PlanMutationsDependencies } from './types';

export default function usePlanMutations({
  dispatch,
  planActions,
  getAccessTokenSilently,
  treatmentPlanId,
  dialog,
}: PlanMutationsDependencies): PlanMutations {
  const { openDialog, closeDialog } = dialog;
  const [isSubmitting, setIsSubmitting] = useState(false);

  const confirmActivate = useCallback(async () => {
    if (treatmentPlanId === undefined) return;
    setIsSubmitting(true);
    const response = await dispatch(
      planActions.activateTreatmentPlan({ getAccessTokenSilently, treatmentPlanId }),
    );
    setIsSubmitting(false);
    if (!response.error) openDialog({ kind: 'activated' });
  }, [dispatch, getAccessTokenSilently, openDialog, planActions, treatmentPlanId]);

  const confirmDeactivate = useCallback(async () => {
    if (treatmentPlanId === undefined) return;
    setIsSubmitting(true);
    const response = await dispatch(
      planActions.deactivateTreatmentPlan({ getAccessTokenSilently, treatmentPlanId }),
    );
    setIsSubmitting(false);
    if (!response.error) closeDialog();
  }, [closeDialog, dispatch, getAccessTokenSilently, planActions, treatmentPlanId]);

  const confirmEdit = useCallback(async () => {
    if (treatmentPlanId === undefined) return;
    setIsSubmitting(true);
    const response = await dispatch(
      planActions.editTreatmentPlan({ getAccessTokenSilently, treatmentPlanId }),
    );
    setIsSubmitting(false);
    if (!response.error) openDialog({ kind: 'editSuccess' });
  }, [dispatch, getAccessTokenSilently, openDialog, planActions, treatmentPlanId]);

  const confirmDelete = useCallback(async () => {
    if (treatmentPlanId === undefined) return;
    closeDialog();
    const response = await dispatch(
      planActions.deleteTreatmentPlan({ getAccessTokenSilently, treatmentPlanId }),
    );
    if (!response.error) history.navigate('/dashboard');
  }, [closeDialog, dispatch, getAccessTokenSilently, planActions, treatmentPlanId]);

  const confirmDownload = useCallback(async () => {
    if (treatmentPlanId === undefined) return;
    closeDialog();
    const response = await dispatch(
      planActions.downloadTreatmentPlan({ getAccessTokenSilently, treatmentPlanId }),
    );
    if (response.payload?.document_url) {
      window.location.href = response.payload.document_url;
      return;
    }
    toast.error(response.error?.message ?? 'Something went wrong while downloading the pdf');
  }, [closeDialog, dispatch, getAccessTokenSilently, planActions, treatmentPlanId]);

  return {
    confirmActivate,
    confirmDeactivate,
    confirmEdit,
    confirmDelete,
    confirmDownload,
    isSubmitting,
  };
}
